import { Private_Routes, Public_Routes } from '@common/url';
import { Avatar } from '@prophecy/ui-v3/Avatar';
import { Button } from '@prophecy/ui-v3/Button';
import { Dropdown } from '@prophecy/ui-v3/Dropdown';
import { HelpCircleIcon, SettingsBIcon, LogOutAIcon } from '@prophecy/ui-v3/Icons';
import { Stack } from '@prophecy/ui-v3/Layout';
import { ProgressBar } from '@prophecy/ui-v3/ProgressBar';
import { theme } from '@prophecy/ui-v3/theme';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography';
import { useLocationPathName } from '@prophecy/utils/history';
import { roundToDecimals } from '@prophecy/utils/number';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useUser } from 'frontend/core/src/context/user';
import { completeLogoutAndReloadPage } from 'frontend/core/src/data/apis/api';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useProfessionalCreditStatus } from './credits/ProfessionalEdition/hooks';
import { useProfessionalEditionCreditsContext } from './credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { isUsageAndBillingPage } from './credits/ProfessionalEdition/utils';

const CREDIT_USAGE_TOOLTIP_WIDTH = 320;
const USER_PROFILE_DROPDOWN_WIDTH = 400;

export const UserProfileDropdown = () => {
  const user = useUser();
  const userName = `${user.user?.firstName} ${user.user?.lastName}`;
  const { totalCreditsAllocated, creditsConsumed, usagePercentage } = useProfessionalCreditStatus();
  const { error } = useProfessionalEditionCreditsContext();
  const navigate = useNavigate();
  const { isProfessionalEditionEnabled } = useGlobalVariables();

  const pathname = useLocationPathName();
  const isOnUsageAndBillingPage = isUsageAndBillingPage(pathname);

  return (
    <Dropdown
      overlay={
        <Stack direction='vertical' gap={theme.spaces.x4}>
          <UserDetails direction='horizontal' gap={theme.spaces.x12} width='100%'>
            <Avatar size='m' text={userName} />
            <Stack direction='vertical' gap={theme.spaces.x2}>
              <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
                {userName}
              </Text>
              <Text level='sm' tone={theme.colors.gray600}>
                {user.user?.email}
              </Text>
            </Stack>
          </UserDetails>
          <Dropdown.Separator />
          {isProfessionalEditionEnabled && (
            <>
              <CreditUsageContainer direction='vertical'>
                <CreditUsage direction='vertical' gap={theme.spaces.x8}>
                  <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center' width='100%'>
                    <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.semiBold}>
                      Credit Usage
                    </Text>
                    <Tooltip
                      title='The total credits consumed by your account. The credit usage is reset every month.'
                      placement='top'
                      overlayStyle={{ width: CREDIT_USAGE_TOOLTIP_WIDTH }}>
                      <span>
                        <StyledHelpCircleIcon type='default' color={theme.colors.gray500} size='xs' />
                      </span>
                    </Tooltip>
                  </Stack>
                  {error ? (
                    <Text level='xs' tone={theme.colors.error500}>
                      {error.message}
                    </Text>
                  ) : (
                    <>
                      <ProgressBar
                        value={usagePercentage}
                        color={theme.colors.brand600}
                        variant={'success'}
                        width='100%'
                      />
                      <Stack direction='horizontal' gap={theme.spaces.x4} align='space-between' alignY='center'>
                        {!isOnUsageAndBillingPage && (
                          <ViewMoreButton
                            variant='link'
                            size='xs'
                            onClick={() => {
                              navigate(Private_Routes.Settings.tab.getUrl({ tab: 'usage&Billing' }));
                            }}
                            data-test-id='view-more-button'>
                            <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
                              View more
                            </Text>
                          </ViewMoreButton>
                        )}
                        <Text level='xs' tone={theme.colors.gray600} weight={theme.fontWeight.normal}>
                          {roundToDecimals(creditsConsumed)} / {roundToDecimals(totalCreditsAllocated)} credits consumed
                          ({usagePercentage}%)
                        </Text>
                      </Stack>
                    </>
                  )}
                </CreditUsage>
              </CreditUsageContainer>

              <Dropdown.Separator />
            </>
          )}
          <Dropdown.Item
            data-test-id='settings-button'
            onClick={() => {
              navigate(Private_Routes.Settings.home.getUrl());
            }}>
            <StyledDropdownItem>
              <SettingsBIcon type='default' color={theme.colors.gray500} />

              <Text level='sm' tone={theme.colors.gray900}>
                Settings
              </Text>
            </StyledDropdownItem>
          </Dropdown.Item>
          <Dropdown.Item
            data-test-id='help-button'
            onClick={() => {
              window.open(Public_Routes.Help.getUrl(), '_blank');
            }}>
            <StyledDropdownItem>
              <HelpCircleIcon type='default' color={theme.colors.gray500} />

              <Text level='sm' tone={theme.colors.gray900}>
                Help
              </Text>
            </StyledDropdownItem>
          </Dropdown.Item>
          <Dropdown.Separator />
          <Dropdown.Item
            data-test-id='logout-button'
            onClick={() => {
              completeLogoutAndReloadPage();
            }}>
            <StyledDropdownItem>
              <LogOutAIcon type='default' color={theme.colors.gray500} />

              <Text level='sm' tone={theme.colors.gray900}>
                Sign out
              </Text>
            </StyledDropdownItem>
          </Dropdown.Item>
        </Stack>
      }
      data-test-id={'professional-edition-header-dropdown'}
      overlayStyle={{
        overflowY: 'auto',
        width: USER_PROFILE_DROPDOWN_WIDTH,
        marginRight: `${theme.spaces.x12}`
      }}
      children={
        <Button variant='plain' size='s' data-test-id='professional-edition-header-dropdown-button'>
          <Avatar size='s' text={userName} />
        </Button>
      }
    />
  );
};

const UserDetails = styled(Stack)`
  padding: ${theme.spaces.x12};
`;

const CreditUsageContainer = styled(Stack)`
  padding: ${theme.spaces.x12};
`;

const CreditUsage = styled(Stack)`
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
  background: ${theme.colors.gray50};
  border-radius: ${theme.radius.x12};
`;

const StyledDropdownItem = styled(Dropdown.Item)`
  gap: ${theme.spaces.x8};
  direction: horizontal;
  align: center;
`;

const ViewMoreButton = styled(Button)`
  color: ${theme.colors.gray600};
  text-decoration: underline;'
  font-weight: ${theme.fontWeight.normal};
`;

const StyledHelpCircleIcon = styled(HelpCircleIcon)`
  cursor: pointer;
`;
